import { CalculateOutlined } from "@mui/icons-material";
import { Suspense } from "react";
import { useSelector } from "react-redux";
import FlxGrid from "../../../../../_src_shared/components/layout/FlxGrid";
import OverviewItem from "../../../../../components/data/OverviewItem";
import RffConditional from "../../../../../components/finalForm/shared/RffConditional";
import ScreenContent from "../../../../../components/layout/ScreenContent";
import LoadingFallback from "../../../../../components/LoadingFallback";
import TitledCard from "../../../../../components/ui/TitledCard";

const SummaryTabLoanTotalsCard = ({ sx }) => {
  // This function is called when user clicks "Save" in the row

  return (
    <TitledCard
      title={"Calculated Loan Totals"}
      icon={<CalculateOutlined className="thin" />}
      variant="h3"
      sx={sx}
    >
      {/* <ScreenContent padding={0.5}> */}
      {/* <ScreenContent padding={1.5}> */}
      <Suspense fallback={<LoadingFallback />}>
        <FlxGrid gap={4} width="150px" sx={{ py: 1 }}>
          <AllFields />
        </FlxGrid>
      </Suspense>
      {/* </ScreenContent> */}
    </TitledCard>
  );
};

const AllFields = ({ children }) => {
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  return (
    <>
      {/* 1. CORE LOAN METRICS - Most Critical */}
      <OverviewItem
        label="Total Loan Amount"
        value={loanDrilldown?.totalLoanAmount}
        type={"dollar"}
      />

      <OverviewItem
        label="Total LTARV"
        tooltipLabel="Total Loan To ARV"
        value={loanDrilldown?.totalLoanToArv}
        type={"percent"}
        showIf={["Fix and Flip", "Ground Up Construction"].includes(
          loanDrilldown?.loanProductType
        )}
      />

      {/* 2. PROPERTY VALUES - Critical for Risk Assessment */}
      <OverviewItem
        label="Total Purchase Price"
        value={loanDrilldown?.totalPurchasePrice}
        type={"dollar"}
        showIf={loanDrilldown?.loanPurpose === "Purchase"}
      />

      <OverviewItem
        label="Total Cost Basis"
        value={loanDrilldown?.totalCostBasis}
        type={"dollar"}
        showIf={["Refinance - No Cash Out", "Refinance - Cash Out"].includes(
          loanDrilldown?.loanPurpose
        )}
      />

      <OverviewItem
        label="Total ARV"
        tooltipLabel="Total After Renovation Value"
        value={loanDrilldown?.totalArv}
        type={"dollar"}
        showIf={["Fix and Flip", "Ground Up Construction"].includes(
          loanDrilldown?.loanProductType
        )}
      />

      <OverviewItem
        label="Total AIV"
        tooltipLabel="Total As Is Value"
        value={loanDrilldown?.totalAsIsValue}
        type={"dollar"}
        showIf={["DSCR", "Fix and Flip", "Stabilized Bridge", "Other"].includes(
          loanDrilldown?.loanProductType
        )}
      />

      {/* 3. PROJECT COSTS - Important for Construction/Rehab Deals */}
      <OverviewItem
        label="Total Construction Budget"
        value={loanDrilldown?.totalConstructionBudget}
        type={"dollar"}
        showIf={["Fix and Flip", "Ground Up Construction"].includes(
          loanDrilldown?.loanProductType
        )}
      />

      <OverviewItem
        label="Total Holdback"
        value={loanDrilldown?.totalHoldback}
        type={"dollar"}
        showIf={["Fix and Flip", "Ground Up Construction"].includes(
          loanDrilldown?.loanProductType
        )}
      />

      {/* 4. OPERATING EXPENSES - Critical for Cash Flow Analysis */}
      <OverviewItem
        label="Total Annual Taxes"
        value={loanDrilldown?.totalAnnualTaxes}
        type={"dollar"}
      />

      <OverviewItem
        label="Total Annual Insurance"
        value={loanDrilldown?.totalAnnualInsurance}
        type={"dollar"}
      />

      <OverviewItem
        label="Total Annual Property Management Fees"
        value={loanDrilldown?.totalAnnualPropertyMgmtFees}
        type={"dollar"}
        showIf={["DSCR", "Stabilized Bridge"].includes(
          loanDrilldown?.loanProductType
        )}
      />

      <OverviewItem
        label="Total Annual HOA Fees"
        value={loanDrilldown?.totalAnnualHoaFees}
        type={"dollar"}
        showIf={["DSCR", "Stabilized Bridge"].includes(
          loanDrilldown?.loanProductType
        )}
      />

      <OverviewItem
        label="Total Annual Flood Insurance"
        value={loanDrilldown?.totalAnnualFloodInsurance}
        type={"dollar"}
        showIf={["DSCR", "Stabilized Bridge"].includes(
          loanDrilldown?.loanProductType
        )}
      />

      <OverviewItem
        label="Total Annual Other Expense"
        value={loanDrilldown?.totalAnnualOtherExpense}
        type={"dollar"}
        showIf={["DSCR", "Stabilized Bridge"].includes(
          loanDrilldown?.loanProductType
        )}
      />

      {/* 5. SECONDARY VALUES - Additional Context */}
      <OverviewItem
        label="Total Secondary ARV"
        tooltipLabel="Total Secondary After Renovation Value"
        value={loanDrilldown?.totalSecondaryArv}
        type={"dollar"}
        showIf={
          loanDrilldown?.totalSecondaryArv != null &&
          loanDrilldown?.totalSecondaryArv !== undefined &&
          loanDrilldown?.totalSecondaryArv > 0
        }
      />

      <OverviewItem
        label="Total Secondary AIV"
        tooltipLabel="Total Secondary As Is Value"
        value={loanDrilldown?.totalSecondaryAsIsValue}
        type={"dollar"}
        showIf={
          loanDrilldown?.totalSecondaryAsIsValue != null &&
          loanDrilldown?.totalSecondaryAsIsValue !== undefined &&
          loanDrilldown?.totalSecondaryAsIsValue > 0
        }
      />

      {/* 6. SPECIAL FEES - Bottom Priority */}
      <OverviewItem
        label="Total Assignment Fees"
        value={loanDrilldown?.totalAssignmentFees}
        type={"dollar"}
        showIf={
          loanDrilldown?.totalAssignmentFees != null &&
          loanDrilldown?.totalAssignmentFees !== undefined &&
          loanDrilldown?.totalAssignmentFees > 0
        }
      />
    </>
  );
};

// Universal Fields - Always show
const UniversalFields = ({ loanDrilldown }) => (
  <>
    <OverviewItem
      label="Total Loan Amount"
      value={loanDrilldown?.totalLoanAmount}
      type={"dollar"}
    />
    <OverviewItem
      label="Total Annual Insurance"
      value={loanDrilldown?.totalAnnualInsurance}
      type={"dollar"}
    />
    <OverviewItem
      label="Total Annual Taxes"
      value={loanDrilldown?.totalAnnualTaxes}
      type={"dollar"}
    />
  </>
);

// Purchase vs Refinance conditional fields
const PurchaseRefinanceFields = ({ loanDrilldown }) => (
  <>
    <OverviewItem
      label="Total Purchase Price"
      value={loanDrilldown?.totalPurchasePrice}
      type={"dollar"}
      showIf={loanDrilldown?.loanPurpose === "Purchase"}
    />

    <OverviewItem
      label="Total Cost Basis"
      value={loanDrilldown?.totalCostBasis}
      type={"dollar"}
      showIf={["Refinance - No Cash Out", "Refinance - Cash Out"].includes(
        loanDrilldown?.loanPurpose
      )}
    />
  </>
);
const PurchaseRefinanceFieldss = ({ loanDrilldown }) => (
  <>
    {/* Show Purchase Price for Purchase loans */}
    <RffConditional field="loanPurpose" operator="==" value="Purchase">
      <OverviewItem
        label="Total Purchase Price"
        value={loanDrilldown?.totalPurchasePrice}
        type={"dollar"}
      />
    </RffConditional>

    {/* Show Cost Basis for Refinance loans */}
    <RffConditional
      field="loanPurpose"
      operator="includes"
      value={["Refinance - No Cash Out", "Refinance - Cash Out"]}
    >
      <OverviewItem
        label="Total Cost Basis"
        value={loanDrilldown?.totalCostBasis}
        type={"dollar"}
        hide={[]}
      />
    </RffConditional>
  </>
);

// DSCR Loan specific fields
const DSCRFields = ({ loanDrilldown }) => (
  <RffConditional field="loanProductType" operator="==" value="DSCR">
    <OverviewItem
      label="Total AIV"
      tooltipLabel="Total As Is Value"
      value={loanDrilldown?.totalAsIsValue}
      type={"dollar"}
    />
    <OverviewItem
      label="Total Annual Property Management Fees"
      value={loanDrilldown?.totalAnnualPropertyMgmtFees}
      type={"dollar"}
    />
    <OverviewItem
      label="Total Annual HOA Fees"
      value={loanDrilldown?.totalAnnualHoaFees}
      type={"dollar"}
    />
    <OverviewItem
      label="Total Annual Other Expense"
      value={loanDrilldown?.totalAnnualOtherExpense}
      type={"dollar"}
    />
    <OverviewItem
      label="Total Annual Flood Insurance"
      value={loanDrilldown?.totalAnnualFloodInsurance}
      type={"dollar"}
    />
  </RffConditional>
);
// DSCR Loan specific fields
const DSCRFieldss = ({ loanDrilldown }) => (
  <RffConditional field="loanProductType" operator="==" value="DSCR">
    <OverviewItem
      label="Total AIV"
      tooltipLabel="Total As Is Value"
      value={loanDrilldown?.totalAsIsValue}
      type={"dollar"}
    />
    <OverviewItem
      label="Total Annual Property Management Fees"
      value={loanDrilldown?.totalAnnualPropertyMgmtFees}
      type={"dollar"}
    />
    <OverviewItem
      label="Total Annual HOA Fees"
      value={loanDrilldown?.totalAnnualHoaFees}
      type={"dollar"}
    />
    <OverviewItem
      label="Total Annual Other Expense"
      value={loanDrilldown?.totalAnnualOtherExpense}
      type={"dollar"}
    />
    <OverviewItem
      label="Total Annual Flood Insurance"
      value={loanDrilldown?.totalAnnualFloodInsurance}
      type={"dollar"}
    />
  </RffConditional>
);

// Fix and Flip specific fields
const FixAndFlipFields = ({ loanDrilldown }) => (
  <RffConditional field="loanProductType" operator="==" value="Fix and Flip">
    <OverviewItem
      label="Total Holdback"
      value={loanDrilldown?.totalHoldback}
      type={"dollar"}
    />
    <OverviewItem
      label="Total LTARV"
      tooltipLabel="Total Loan To ARV"
      value={loanDrilldown?.totalLoanToArv}
      type={"percent"}
    />
    <OverviewItem
      label="Total ARV"
      tooltipLabel="Total After Renovation Value"
      value={loanDrilldown?.totalArv}
      type={"dollar"}
    />
    <OverviewItem
      label="Total AIV"
      tooltipLabel="Total As Is Value"
      value={loanDrilldown?.totalAsIsValue}
      type={"dollar"}
    />
    <OverviewItem
      label="Total Construction Budget"
      value={loanDrilldown?.totalConstructionBudget}
      type={"dollar"}
    />
  </RffConditional>
);

// Ground Up Construction specific fields
const GroundUpConstructionFields = ({ loanDrilldown }) => (
  <RffConditional
    field="loanProductType"
    operator="=="
    value="Ground Up Construction"
  >
    <OverviewItem
      label="Total Holdback"
      value={loanDrilldown?.totalHoldback}
      type={"dollar"}
    />
    <OverviewItem
      label="Total LTARV"
      tooltipLabel="Total Loan To ARV"
      value={loanDrilldown?.totalLoanToArv}
      type={"percent"}
    />
    <OverviewItem
      label="Total ARV"
      tooltipLabel="Total After Renovation Value"
      value={loanDrilldown?.totalArv}
      type={"dollar"}
    />
    <OverviewItem
      label="Total Construction Budget"
      value={loanDrilldown?.totalConstructionBudget}
      type={"dollar"}
    />
  </RffConditional>
);

// Stabilized Bridge specific fields
const StabilizedBridgeFields = ({ loanDrilldown }) => (
  <RffConditional
    field="loanProductType"
    operator="=="
    value="Stabilized Bridge"
  >
    <OverviewItem
      label="Total AIV"
      tooltipLabel="Total As Is Value"
      value={loanDrilldown?.totalAsIsValue}
      type={"dollar"}
    />
    <OverviewItem
      label="Total Annual Property Management Fees"
      value={loanDrilldown?.totalAnnualPropertyMgmtFees}
      type={"dollar"}
    />
    <OverviewItem
      label="Total Annual HOA Fees"
      value={loanDrilldown?.totalAnnualHoaFees}
      type={"dollar"}
    />
    <OverviewItem
      label="Total Annual Other Expense"
      value={loanDrilldown?.totalAnnualOtherExpense}
      type={"dollar"}
    />
    <OverviewItem
      label="Total Annual Flood Insurance"
      value={loanDrilldown?.totalAnnualFloodInsurance}
      type={"dollar"}
    />
  </RffConditional>
);

// Other product type fields
const OtherProductTypeFields = ({ loanDrilldown }) => (
  <RffConditional field="loanProductType" operator="==" value="Other">
    <OverviewItem
      label="Total AIV"
      tooltipLabel="Total As Is Value"
      value={loanDrilldown?.totalAsIsValue}
      type={"dollar"}
    />
  </RffConditional>
);

// Secondary fields - only show if data exists
const SecondaryFields = ({ loanDrilldown }) => (
  <>
    {/* Show Secondary ARV only if it has a value */}
    <RffConditional
      condition={{
        operator: "AND",
        rules: [
          {
            field: "loanDrilldown.totalSecondaryArv",
            operator: "!=",
            value: null,
          },
          {
            field: "loanDrilldown.totalSecondaryArv",
            operator: "!=",
            value: undefined,
          },
          { field: "loanDrilldown.totalSecondaryArv", operator: ">", value: 0 },
        ],
      }}
    >
      <OverviewItem
        label="Total Secondary ARV"
        tooltipLabel="Total Secondary After Renovation Value"
        value={loanDrilldown?.totalSecondaryArv}
        type={"dollar"}
      />
    </RffConditional>

    {/* Show Secondary AIV only if it has a value */}
    <RffConditional
      condition={{
        operator: "AND",
        rules: [
          {
            field: "loanDrilldown.totalSecondaryAsIsValue",
            operator: "!=",
            value: null,
          },
          {
            field: "loanDrilldown.totalSecondaryAsIsValue",
            operator: "!=",
            value: undefined,
          },
          {
            field: "loanDrilldown.totalSecondaryAsIsValue",
            operator: ">",
            value: 0,
          },
        ],
      }}
    >
      <OverviewItem
        label="Total Secondary AIV"
        tooltipLabel="Total Secondary As Is Value"
        value={loanDrilldown?.totalSecondaryAsIsValue}
        type={"dollar"}
      />
    </RffConditional>
  </>
);

// Assignment fees - typically for wholesale deals
const AssignmentFeesFields = ({ loanDrilldown }) => (
  <RffConditional
    condition={{
      operator: "AND",
      rules: [
        {
          field: "loanDrilldown.totalAssignmentFees",
          operator: "!=",
          value: null,
        },
        {
          field: "loanDrilldown.totalAssignmentFees",
          operator: "!=",
          value: undefined,
        },
        { field: "loanDrilldown.totalAssignmentFees", operator: ">", value: 0 },
      ],
    }}
  >
    <OverviewItem
      label="Total Assignment Fees"
      value={loanDrilldown?.totalAssignmentFees}
      type={"dollar"}
      show={loanDrilldown?.loanProductType === "DSCR"}
    />
  </RffConditional>
);
{
  /* <FlxGrid gap={4} width="150px">
            <OverviewItem
              label="Total Loan Amount"
              value={loanDrilldown?.totalLoanAmount}
              type={"dollar"}
            />
            <OverviewItem
              label="Total Holdback"
              value={loanDrilldown?.totalHoldback}
              type={"dollar"}
            />

            <OverviewItem
              label="Total LTARV"
              tooltipLabel="Total Loan To ARV"
              value={loanDrilldown?.totalLoanToArv}
              type={"percent"}
            />
            <OverviewItem
              label="Total ARV"
              tooltipLabel="Total After Renovation Value"
              value={loanDrilldown?.totalArv}
              type={"dollar"}
              hide
            />
            <OverviewItem
              label="Total Secondary ARV"
              tooltipLabel="Total Secondary After Renovation Value"
              value={loanDrilldown?.totalSecondaryArv}
              type={"dollar"}
            />
            <OverviewItem
              label="Total AIV"
              tooltipLabel="Total As Is Value"
              value={loanDrilldown?.totalAsIsValue}
              type={"dollar"}
            />
            <OverviewItem
              label="Total Secondary AIV"
              tooltipLabel="Total Secondary As Is Value"
              value={loanDrilldown?.totalSecondaryAsIsValue}
              type={"dollar"}
            />

            <OverviewItem
              label="Total Purchase Price"
              value={loanDrilldown?.totalPurchasePrice}
              type={"dollar"}
            />
            <OverviewItem
              label="Total Construction Budget"
              value={loanDrilldown?.totalConstructionBudget}
              type={"dollar"}
            />

            <OverviewItem
              label="Total Cost Basis"
              value={loanDrilldown?.totalCostBasis}
              type={"dollar"}
            />

       
            <OverviewItem
              label="Total Annual Flood Insurance"
              value={loanDrilldown?.totalAnnualFloodInsurance}
              type={"dollar"}
            />

            <OverviewItem
              label="Total Annual HOA Fees"
              value={loanDrilldown?.totalAnnualHoaFees}
              type={"dollar"}
            />

            <OverviewItem
              label="Total Annual Insurance"
              value={loanDrilldown?.totalAnnualInsurance}
              type={"dollar"}
            />

            <OverviewItem
              label="Total Annual Other Expense"
              value={loanDrilldown?.totalAnnualOtherExpense}
              type={"dollar"}
            />

            <OverviewItem
              label="Total Annual Property Management Fees"
              value={loanDrilldown?.totalAnnualPropertyMgmtFees}
              type={"dollar"}
            />

            <OverviewItem
              label="Total Annual Taxes"
              value={loanDrilldown?.totalAnnualTaxes}
              type={"dollar"}
            />

            <OverviewItem
              label="Total Assignment Fees"
              value={loanDrilldown?.totalAssignmentFees}
              type={"dollar"}
            />
          </FlxGrid> */
}
export default SummaryTabLoanTotalsCard;
